/**
 * "Waiting for the card…" — ONE implementation of the desk's side of a tap.
 *
 * A surface starts a charge on its own endpoint, gets back
 * `{ paymentIntentId, readerId }`, and hands them to `run`. This hook shows
 * the wait (via `readerWait`, rendered by ReaderWaitPanel), polls until the
 * terminal answers, and resolves with the outcome. `cancel` stops collecting
 * on the reader; if the tap beat the cancel, the charge is recorded and the
 * outcome is success — a real charge is never reported as canceled.
 */
import { useCallback, useRef, useState } from "react";
import { cancelReaderCharge, simulateReaderTap } from "../api/terminalAPI";
import { waitForReaderCharge, ReaderChargeOutcome } from "../utils/readerCharge";

export interface ReaderWaitState {
  paymentIntentId: string;
  readerId: string;
  readerLabel?: string | null;
  simulated?: boolean;
  amount?: number | null;
  message: string | null;
}

export interface StartReaderWait {
  paymentIntentId: string;
  readerId: string;
  readerLabel?: string | null;
  simulated?: boolean;
  amount?: number | null;
}

export function useReaderWait(token: string | null | undefined) {
  const [readerWait, setReaderWait] = useState<ReaderWaitState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const currentRef = useRef<ReaderWaitState | null>(null);

  const run = useCallback(
    async (start: StartReaderWait): Promise<ReaderChargeOutcome> => {
      const ac = new AbortController();
      abortRef.current = ac;
      const state: ReaderWaitState = { ...start, message: "Waiting for the card…" };
      currentRef.current = state;
      setReaderWait(state);
      try {
        return await waitForReaderCharge(token || "", start.paymentIntentId, {
          signal: ac.signal,
          onTick: (m) =>
            setReaderWait((w) => (w ? { ...w, message: m || w.message } : w)),
        });
      } finally {
        currentRef.current = null;
        abortRef.current = null;
        setReaderWait(null);
      }
    },
    [token]
  );

  const cancel = useCallback(async () => {
    const w = currentRef.current;
    if (!w) return;
    setReaderWait((s) => (s ? { ...s, message: "Canceling…" } : s));
    const res = await cancelReaderCharge(token || "", w.paymentIntentId, w.readerId);
    // A tap that landed before the cancel is a real charge: let the poll
    // pick up the recorded success rather than aborting into "canceled".
    if (res?.success && res.status === "succeeded") return;
    abortRef.current?.abort();
  }, [token]);

  const simulateTap = useCallback(async () => {
    const w = currentRef.current;
    if (!w) return;
    await simulateReaderTap(token || "", w.readerId);
  }, [token]);

  return { readerWait, run, cancel, simulateTap };
}
